import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { SettingService } from 'src/app/services/setting/setting.service';
import Swal from 'sweetalert2';

interface CustomDependingM3Item {
  from: number;
  to: number;
  amount: number;
}

interface IslCommissionItem {
  from: number;
  to: number;
  type: 'percentage' | 'fixed';
  amount: number;
}

@Component({
  selector: 'app-cal-other-cf-charges',
  templateUrl: './cal-other-cf-charges.component.html',
  styleUrls: ['./cal-other-cf-charges.component.scss']
})
export class CalOtherCfChargesComponent extends DestructorComponent implements OnInit {

  otherCfChargesForm!: FormGroup;
  isLoading = false;
  data: any;
  settingId: string = '';

  // Dynamic arrays for form fields
  customDependingM3Items: CustomDependingM3Item[] = [];
  islCommissionItems: IslCommissionItem[] = [];

  constructor(
    private fb: FormBuilder,
    private _globalService: GlobalStateService,
    private _settingService: SettingService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.initializeArrays();
    this.GetData();
  }

  private initializeForm(): void {
    this.otherCfChargesForm = this.fb.group({
      car_tax_avg: ['', [Validators.min(0)]],
      recycle_fee: ['', [Validators.min(0)]],
      insurance_if: ['', [Validators.min(0)]],
      other_if: ['', [Validators.min(0)]]
    });
  }

  private initializeArrays(): void {
    // Initialize with one item each for Custom Depending on M3 and ISL Commission
    this.customDependingM3Items = [{ from: 0, to: 0, amount: 0 }];
    this.islCommissionItems = [{ from: 0, to: 0, type: 'percentage', amount: 0 }];
  }

  // Getter methods for form controls
  get carTaxAvg() {
    return this.otherCfChargesForm.get('car_tax_avg');
  }

  get recycleFee() {
    return this.otherCfChargesForm.get('recycle_fee');
  }

  get insuranceIf() {
    return this.otherCfChargesForm.get('insurance_if');
  }

  get otherIf() {
    return this.otherCfChargesForm.get('other_if');
  }

  GetData(): void {
    this.subscriptions.push(
      this._globalService.GetSetting().subscribe((res: any) => {
        this.data = res.other_cf_charges;
        this.settingId = res._id;
        console.log('Other CF Charges data:', this.data);

        // Populate form with existing data
        if (this.data) {
          this.otherCfChargesForm.patchValue({
            car_tax_avg: this.data.car_tax_avg || '',
            recycle_fee: this.data.recycle_fee || '',
            insurance_if: this.data.insurance_if || '',
            other_if: this.data.other_if || ''
          });

          // Populate dynamic arrays
          if (this.data.custom_depending_m3 && this.data.custom_depending_m3.length > 0) {
            this.customDependingM3Items = this.data.custom_depending_m3;
          }

          if (this.data.isl_commission && this.data.isl_commission.length > 0) {
            this.islCommissionItems = this.data.isl_commission;
          }
        }
      })
    );
  }

  // Custom Depending on M3 methods
  addCustomDependingM3Item(): void {
    this.customDependingM3Items.push({ from: 0, to: 0, amount: 0 });
  }

  removeCustomDependingM3Item(index: number): void {
    if (this.customDependingM3Items.length > 1) {
      this.customDependingM3Items.splice(index, 1);
    }
  }

  // ISL Commission methods
  addIslCommissionItem(): void {
    this.islCommissionItems.push({ from: 0, to: 0, type: 'percentage', amount: 0 });
  }

  removeIslCommissionItem(index: number): void {
    if (this.islCommissionItems.length > 1) {
      this.islCommissionItems.splice(index, 1);
    }
  }

  // Validation methods for ISL Commission
  getAmountMin(item: IslCommissionItem): number {
    return 0;
  }

  getAmountMax(item: IslCommissionItem): number | null {
    return item.type === 'percentage' ? 100 : null;
  }

  getAmountLabel(item: IslCommissionItem): string {
    return item.type === 'percentage' ? '%' : 'JPY';
  }

  isAmountValid(item: IslCommissionItem): boolean {
    if (item.type === 'percentage') {
      return item.amount >= 0 && item.amount <= 100;
    } else {
      return item.amount > 0;
    }
  }

  onSubmit(): void {
    if (this.otherCfChargesForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.otherCfChargesForm.value;

      const submitData = {
        "other_cf_charges": {
          custom_depending_m3: this.customDependingM3Items.filter(item =>
            item.from !== null && item.to !== null && item.amount !== null
          ),
          isl_commission: this.islCommissionItems.filter(item =>
            item.from !== null && item.to !== null && item.amount !== null
          ),
          car_tax_avg: formData.car_tax_avg || '',
          recycle_fee: formData.recycle_fee || '',
          insurance_if: formData.insurance_if || '',
          other_if: formData.other_if || ''
        }
      };

      this.subscriptions.push(
        this._settingService.UpdateData(this.settingId, submitData).subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Other CF Charges updated successfully:', response);

            this._globalService.SetSetting();
            // Show success message
            Swal.fire({
              text: "Other CF Charges have been successfully updated!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Error updating Other CF Charges:', error);

            // Show error message
            Swal.fire({
              text: "Sorry, looks like there are some errors detected, please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    }
  }
}
